import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  User as UserIcon,
  Users,
  ShieldCheck,
  ImageIcon,
  Mail,
  Trash2,
  Fingerprint,
  ChevronRight,
  Info,
  Download,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import PWAInstallButton from "@/components/PWAInstallButton";
import { toImageDataUrl } from "@/lib/image";
import { subscribeQueue } from "@/lib/syncQueue";
import { can, roleLabel } from "@/lib/permissions";

const APP_VERSION = "1.4";

export default function Profil() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const branding = useBranding();
  const isAdmin = user?.role === "admin";

  const [biometricSupported, setBiometricSupported] = useState(false);
  const [pendingOps, setPendingOps] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribeQueue(({ pending }) => setPendingOps(pending));
    return unsubscribe;
    // `subscribeQueue` is an imported singleton and `setPendingOps` is
    // stable — this subscription only needs to fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Detect WebAuthn / platform authenticator support
    if (
      typeof window !== "undefined" &&
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential
        .isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
      window.PublicKeyCredential
        .isUserVerifyingPlatformAuthenticatorAvailable()
        .then((ok) => setBiometricSupported(Boolean(ok)))
        .catch(() => setBiometricSupported(false));
    }
  }, []);

  const handleLogout = () => {
    if (!window.confirm("Voulez-vous vraiment vous déconnecter ?")) return;
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div data-testid="profil-page" className="bg-[var(--vidoc-bg-secondary)] min-h-full">
      {/* Header avec logo + identité */}
      <section className="bg-white border-b border-[var(--vidoc-border)] py-8 flex flex-col items-center">
        {branding.icon_base64 ? (
          <img
            src={toImageDataUrl(branding.icon_base64)}
            alt=""
            className="w-36 h-36 rounded-2xl object-contain bg-white mb-3"
          />
        ) : (
          <div className="w-36 h-36 rounded-2xl bg-[var(--vidoc-primary)] flex items-center justify-center mb-3">
            <UserIcon className="w-16 h-16 text-white" />
          </div>
        )}
        <div className="text-lg font-bold text-[var(--vidoc-primary)] mb-1">
          {branding.company_name || "Maison de ventes"}
        </div>
        <div className="text-2xl font-semibold" data-testid="profil-username">
          {user?.username}
        </div>
        <div className="text-base text-[var(--vidoc-text-secondary)] mt-1 flex items-center gap-1">
          {isAdmin && <ShieldCheck className="w-4 h-4" />}
          {roleLabel(user?.role)}
        </div>
      </section>

      {/* GESTION (filtré par rôle) */}
      <SectionGroup title="Gestion">
        {can(user, "client.read") && (
          <MenuItem
            icon={Users}
            iconColor="text-[var(--vidoc-primary)]"
            label="Gérer les clients"
            onClick={() => navigate("/clients")}
            testid="menu-clients"
          />
        )}
        <MenuItem
          icon={Inbox}
          iconColor="text-[var(--vidoc-primary)]"
          label="File d'attente"
          onClick={() => navigate("/queue")}
          testid="menu-queue"
          badge={pendingOps > 0 ? pendingOps : null}
        />
      </SectionGroup>

      {/* ADMINISTRATION (admin only) */}
      {isAdmin && (
        <SectionGroup title="Administration" testid="admin-section">
          <MenuItem
            icon={ShieldCheck}
            iconColor="text-[var(--vidoc-warning)]"
            label="Gérer les utilisateurs"
            onClick={() => navigate("/admin/users")}
            testid="menu-admin-users"
          />
          <MenuItem
            icon={ImageIcon}
            iconColor="text-[var(--vidoc-warning)]"
            label="Gérer les icônes"
            onClick={() => navigate("/admin/icons")}
            testid="menu-admin-icons"
          />
          <MenuItem
            icon={Mail}
            iconColor="text-[var(--vidoc-warning)]"
            label="Gérer les sauvegardes"
            onClick={() => navigate("/admin/backup-emails")}
            testid="menu-admin-backup"
          />
          <MenuItem
            icon={Trash2}
            iconColor="text-[var(--vidoc-warning)]"
            label="Purge des données"
            onClick={() => navigate("/admin/purge")}
            testid="menu-admin-purge"
          />
        </SectionGroup>
      )}

      {/* SÉCURITÉ */}
      <SectionGroup title="Sécurité">
        <div
          className="bg-white px-4 py-4 border-b border-[var(--vidoc-border)] flex items-center gap-3"
          data-testid="biometric-status"
        >
          <Fingerprint
            className={`w-6 h-6 ${
              biometricSupported
                ? "text-[var(--vidoc-success)]"
                : "text-[var(--vidoc-text-tertiary)]"
            }`}
          />
          <div className="flex-1">
            <div className="text-base">
              {biometricSupported
                ? "Biométrie disponible"
                : "Biométrie non disponible"}
            </div>
            <div className="text-xs text-[var(--vidoc-text-secondary)]">
              {biometricSupported
                ? "Touch ID / Face ID supporté sur cet appareil"
                : "Cet appareil ou navigateur ne supporte pas la biométrie"}
            </div>
          </div>
        </div>
      </SectionGroup>

      {/* INSTALLATION PWA */}
      <SectionGroup title="Application">
        <div className="bg-white px-4 py-4 border-b border-[var(--vidoc-border)] flex items-center gap-3">
          <Download className="w-6 h-6 text-[var(--vidoc-primary)]" />
          <div className="flex-1">
            <div className="text-base mb-1">Installer sur l'écran d'accueil</div>
            <PWAInstallButton className="text-xs" />
          </div>
        </div>
      </SectionGroup>

      {/* À PROPOS */}
      <SectionGroup title="À propos">
        <div
          className="bg-white px-4 py-5 border-b border-[var(--vidoc-border)] flex flex-col items-center text-center"
          data-testid="about-section"
        >
          <Info className="w-6 h-6 text-[var(--vidoc-primary)] mb-2" />
          <div className="text-lg font-semibold">VIDOC</div>
          <div className="text-sm text-[var(--vidoc-text-secondary)]">
            Gestion des documents client
          </div>
          <div className="text-sm text-[var(--vidoc-text-secondary)]">
            maison de vente aux enchères
          </div>
          <div className="text-xs text-[var(--vidoc-text-tertiary)] mt-2">
            Version {APP_VERSION}
          </div>
        </div>
      </SectionGroup>

      {/* DÉCONNEXION */}
      <div className="px-4 mt-6">
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-white text-[var(--vidoc-danger)] font-semibold py-4 rounded-xl border border-[var(--vidoc-border)] hover:bg-red-50 touch-target"
        >
          <LogOut className="w-5 h-5" />
          Déconnexion
        </button>
      </div>

      <footer className="text-center py-6 text-xs text-[var(--vidoc-text-tertiary)]">
        © 2026 François Meyniel
      </footer>
    </div>
  );
}

function SectionGroup({ title, children, testid }) {
  return (
    <section className="mt-6" data-testid={testid}>
      <h3 className="px-4 mb-2 text-xs font-semibold text-[var(--vidoc-text-tertiary)] uppercase tracking-wider">
        {title}
      </h3>
      <div className="bg-white">{children}</div>
    </section>
  );
}

function MenuItem({ icon: Icon, iconColor, label, onClick, testid, badge }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-4 border-b border-[var(--vidoc-border)] hover:bg-[var(--vidoc-bg-secondary)] active:bg-[var(--vidoc-bg-tertiary)] touch-target text-left"
    >
      <Icon className={`w-6 h-6 ${iconColor}`} />
      <span className="flex-1 text-base">{label}</span>
      {badge != null && (
        <span
          data-testid={`${testid}-badge`}
          className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-[var(--vidoc-warning)] text-white text-xs font-bold"
        >
          {badge}
        </span>
      )}
      <ChevronRight className="w-5 h-5 text-[var(--vidoc-text-tertiary)]" />
    </button>
  );
}
